// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@stockpilot/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  /**
   * Optional richer SVG illustration shown in place of the icon ring. Use the
   * theme-aware illustrations from `components/ui/illustrations`.
   */
  illustration?: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  /** Optional call-to-action (e.g. a "Go back" button) rendered below the copy. */
  action?: JSX.Element;
  /**
   * When provided, the illustrative icon/illustration becomes an interactive
   * target that triggers the same action as the primary CTA (open a dialog,
   * navigate, or focus an input) so the empty state never feels like a dead end.
   * Tapping the mark is a discoverable shortcut to the CTA — leave it undefined
   * for purely informational empty states (the mark stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive mark (used when `onIconActivate` is
   * set). Should describe the action, e.g. "Add your first product". Falls back
   * to the title.
   */
  iconActionLabel?: string;
  class?: string;
}

export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    {/* When an action is wired, the mark becomes a real button so tap/click/
        keyboard (Enter/Space, native to <button>) triggers the CTA. Otherwise it
        stays a decorative div. The float animation is suppressed under the global
        prefers-reduced-motion guard; the hover scale is motion-safe only. */}
    <Show
      when={props.onIconActivate}
      fallback={
        <Show
          when={props.illustration}
          fallback={
            <div class="animate-scale-in">
              <div class="mb-6 rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float">
                <props.icon class="h-12 w-12 text-primary/60" />
              </div>
            </div>
          }
        >
          {(Illustration) => (
            <div class="animate-scale-in">
              <div class="mb-6 animate-float">
                {/* `Show` hands back an accessor; call it to get the component. */}
                {(() => {
                  const Comp = Illustration();
                  return <Comp class="h-36 w-36" />;
                })()}
              </div>
            </div>
          )}
        </Show>
      }
    >
      {(activate) => (
        <div class="animate-scale-in">
          <button
            type="button"
            aria-label={props.iconActionLabel ?? props.title}
            onClick={() => {
              activate()();
            }}
            class="group mb-6 rounded-full outline-none transition-transform duration-200 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            <Show
              when={props.illustration}
              fallback={
                <div class="rounded-full bg-primary/5 p-6 ring-1 ring-primary/10 animate-float transition-colors group-hover:bg-primary/10 group-hover:ring-primary/20">
                  <props.icon class="h-12 w-12 text-primary/60 transition-colors group-hover:text-primary" />
                </div>
              }
            >
              {(Illustration) => (
                <div class="animate-float">
                  {(() => {
                    const Comp = Illustration();
                    return <Comp class="h-36 w-36" />;
                  })()}
                </div>
              )}
            </Show>
          </button>
        </div>
      )}
    </Show>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-6">{props.action}</div>
    </Show>
  </div>
);
